import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  CalendarDays,
  Hash,
  Loader2,
  RefreshCw,
  Send,
} from 'lucide-react';

import {
  listChannelMessages,
  listChannels,
  postToChannel,
} from '../../services/teamsDmApi';
import MessageBubble, { SystemEvent } from './MessageBubble';
import MeetingDetailModal from '../meeting/MeetingDetailModal';
import { useWebSocket } from '../../context/WebSocketProvider';

const MAX_CHARS = 4000;

/** A scheduled-meeting post, drawn as Teams does: title and Join. */
const MeetingPost = ({ meeting }) => (
  <div className="mt-1 flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-2">
    <CalendarDays className="h-4 w-4 shrink-0 text-[#8b88f0]" />
    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-zinc-100">
      {meeting.title}
    </span>
    {meeting.join_url && (
      <a
        href={meeting.join_url}
        target="_blank"
        rel="noreferrer"
        className="rounded-md border border-zinc-600 px-2 py-0.5 text-[12px] text-zinc-200 hover:bg-zinc-800"
      >
        Join
      </a>
    )}
  </div>
);

const Note = ({ children }) => (
  <p className="px-3 py-6 text-center text-[13px] text-zinc-400">{children}</p>
);

/** One channel: newest posts with their replies; post or reply. */
const ChannelView = ({ accessToken, team, channel, onBack }) => {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    setError(null);
    listChannelMessages(accessToken, team.id, channel.id)
      .then((res) => setPosts(res?.data || []))
      .catch((err) => {
        setPosts([]);
        setError(err || {});
      });
  }, [accessToken, team.id, channel.id]);

  useEffect(() => {
    load();
  }, [load]);

  // A new post or reply in this channel (live subscription made on open).
  const { subscribe, isConnected } = useWebSocket() || {};
  useEffect(() => {
    if (!subscribe || !isConnected) return undefined;
    return subscribe({
      teams_chat_changed: (data) => {
        if (data?.team_id === team.id && data?.channel_id === channel.id)
          load();
      },
    });
  }, [subscribe, isConnected, load, team.id, channel.id]);

  const value = text.trim();
  const send = async () => {
    if (!value || value.length > MAX_CHARS || sending) return;
    setSending(true);
    try {
      await postToChannel(accessToken, team.id, channel.id, value, replyTo?.id);
      setText('');
      setReplyTo(null);
      load();
    } catch (err) {
      // The text stays in the box so nothing typed is lost.
      toast.error(`Could not post: ${err?.message || 'try again'}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-2">
        <button
          type="button"
          aria-label="Back to channels"
          onClick={onBack}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-zinc-100">
          {team.name} · {channel.name}
        </span>
        <button
          type="button"
          aria-label="Refresh channel"
          onClick={load}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {posts === null && <Note>Loading posts…</Note>}
        {error && (
          <Note>{error?.message || 'Could not load this channel.'}</Note>
        )}
        {posts?.length === 0 && !error && <Note>No posts yet.</Note>}
        <ul className="flex flex-col gap-3" aria-label="Posts">
          {(posts || []).map((post) => (
            <li key={post.id} className="rounded-lg border border-zinc-800 p-2">
              <ul className="flex flex-col gap-2">
                {post.system ? (
                  <SystemEvent message={post} title={channel.name} />
                ) : (
                  <MessageBubble accessToken={accessToken} message={post} />
                )}
                {post.meeting && <MeetingPost meeting={post.meeting} />}
                {post.replies.map((reply) =>
                  // A meeting's call events: lines and a card, as in chats.
                  reply.system ? (
                    <SystemEvent
                      key={reply.id}
                      message={reply}
                      title={post.meeting?.title}
                      onOpenSummary={setSummary}
                    />
                  ) : (
                    <div key={reply.id} className="pl-4">
                      <MessageBubble
                        accessToken={accessToken}
                        message={reply}
                      />
                    </div>
                  ),
                )}
              </ul>
              <button
                type="button"
                onClick={() => setReplyTo(post)}
                className="mt-1 text-[11px] text-indigo-300 hover:underline"
              >
                Reply
              </button>
            </li>
          ))}
        </ul>
      </div>
      {summary && (
        <MeetingDetailModal
          eventId={summary}
          onClose={() => setSummary(null)}
        />
      )}
      <div className="shrink-0 border-t border-zinc-800 px-3 py-2">
        {replyTo && (
          <p className="mb-1 flex items-center justify-between text-[11px] text-zinc-400">
            <span className="truncate">
              Replying to {replyTo.from}: {replyTo.text.slice(0, 60)}
            </span>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              className="ml-2 shrink-0 text-zinc-500 hover:text-zinc-200"
            >
              Cancel
            </button>
          </p>
        )}
        <textarea
          aria-label="Channel message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder={replyTo ? 'Write a reply' : 'Start a new post'}
          disabled={sending}
          className="w-full rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF] disabled:opacity-60"
        />
        <div className="mt-1.5 flex justify-end">
          <button
            type="button"
            onClick={send}
            disabled={!value || value.length > MAX_CHARS || sending}
            className="flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            {replyTo ? 'Reply' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** The member's teams and channels; pick one to read and post. */
const ChannelsView = ({ accessToken }) => {
  const [teams, setTeams] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    listChannels(accessToken)
      .then((res) => setTeams(res?.data || []))
      .catch((err) => {
        setTeams([]);
        setError(err || {});
      });
  }, [accessToken]);

  if (open) {
    return (
      <ChannelView
        accessToken={accessToken}
        team={open.team}
        channel={open.channel}
        onBack={() => setOpen(null)}
      />
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {teams === null && <Note>Loading channels…</Note>}
      {error && <Note>{error?.message || 'Could not load channels.'}</Note>}
      {teams?.length === 0 && !error && <Note>You are not in any team.</Note>}
      {(teams || []).map((team) => (
        <div key={team.id} className="border-b border-zinc-800 py-1">
          <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            {team.name}
          </p>
          {team.channels.map((channel) => (
            <button
              key={channel.id}
              type="button"
              onClick={() => setOpen({ team, channel })}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-zinc-200 hover:bg-zinc-800/50"
            >
              <Hash className="h-3.5 w-3.5 text-zinc-500" />
              <span className="truncate">{channel.name}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ChannelsView;
